import { getUserProfile } from 'helpers/userProfile';
import { t } from 'i18next';
import {
  ReactElement, useEffect, useMemo, useState,
} from 'react';
import { useHistory } from 'react-router-dom';
import { colors } from '~/styles/colors';
import { useFilterContext } from '../contexts/FilterContext';
import { useThermalComfortDataContext } from '../contexts/ThermalComfortDataContext';
import { handleFilterStateRoute } from '../helpers/filters';
import { normalizeNumber } from '../helpers/numbers';
import { formatNumberWithFractionDigits } from '~/helpers/thousandFormatNumber';
import { canViewThermalComfort } from '../helpers/thermalComfort';
import { BigNumberCard } from './components/Card';
import {
  ThermalComfortTooltipContent,
  ThermalComfortTooltipDivider,
  ThermalComfortTooltipIntro,
  ThermalComfortTooltipSectionTitle,
  ThermalComfortTooltipList,
  ThermalComfortTooltipListItem,
  ThermalComfortTooltipFooter,
} from './styles';

interface ThermalComfortProps {
  hasThermalComfort: boolean;
  loading: boolean;
}

export function ThermalComfort({ hasThermalComfort, loading }: Readonly<ThermalComfortProps>): ReactElement {
  const history = useHistory();
  const {
    loading: loadingThermalComfort, units, refetch,
  } = useThermalComfortDataContext();
  const [thermalComfortInfo, setThermalComfortInfo] = useState<{ averageIndex: number, acceptableUnits: number } | undefined>();
  const { selectedOptions, clients, filtersDependencies } = useFilterContext();
  const [profile] = useState(getUserProfile());

  const generalLoading = useMemo(
    () => loading || (loadingThermalComfort && hasThermalComfort && canViewThermalComfort(profile)),
    [hasThermalComfort, loading, loadingThermalComfort],
  );

  function parseThermalComfortInfo(unitsList: typeof units) {
    const nonInoperative = unitsList.filter((u) => u.thermalComfort !== 'inoperative');

    if (nonInoperative.length === 0) {
      setThermalComfortInfo({ averageIndex: 0, acceptableUnits: 0 });
      return;
    }

    const averageIndex = nonInoperative.reduce((sum, u) => sum + u.unitIndex, 0) / nonInoperative.length;
    const acceptableUnits = unitsList.filter(
      (u) => u.thermalComfort === 'comfortable',
    ).length;

    setThermalComfortInfo({ averageIndex, acceptableUnits });
  }

  function indicatorColor(): string {
    if (!thermalComfortInfo) return colors.icon['disabled-300'];

    if (thermalComfortInfo.averageIndex > 7.0) return colors.state.success.base;
    if (thermalComfortInfo.averageIndex >= 5.0) return colors.state.warning.base;
    return colors.state.danger.base;
  }

  const comfortableUnitsCount = useMemo(
    () => units.filter((unit) => unit.thermalComfort === 'comfortable').length,
    [units],
  );

  const canSeeDetails = comfortableUnitsCount > 0;

  function onClickDetails(): void {
    history.push('/analise/conforto-termico');
  }

  useEffect(() => {
    if (hasThermalComfort && canViewThermalComfort(profile) && clients && clients.length > 0) {
      const clientIds = clients.map(Number);
      refetch(clientIds, selectedOptions);
    }
  }, [filtersDependencies]);

  useEffect(() => {
    if (canViewThermalComfort(profile)) {
      parseThermalComfortInfo(units);
    }
  }, [units]);

  return (
    <BigNumberCard
      id="thermal-comfort"
      icon="Thermometer2Icon"
      number={formatNumberWithFractionDigits(thermalComfortInfo?.averageIndex ?? 0, { minimum: 1, maximum: 1 })}
      subnumber={!selectedOptions.status ? '/10' : undefined}
      description={{
        text: t('confortoTermicoMedido'),
        tooltip: <ThermalComfortTooltip />,
      }}
      subDescription={{
        number: normalizeNumber(thermalComfortInfo?.acceptableUnits),
        text: t('unidadesComConfortoBom'),
      }}
      loading={generalLoading}
      disabledTooltip={false}
      indicatorColor={indicatorColor()}
      needUpgrade={hasThermalComfort ? undefined : 'thermalComfort'}
      onClickDetails={hasThermalComfort && canSeeDetails ? onClickDetails : undefined}
      detailsDisabledReasonKey={hasThermalComfort && !canSeeDetails ? 'semUnidadesParaVerDetalhes' : undefined}
      dataTestId="big-number-card-thermal-comfort"
      comingSoon={!canViewThermalComfort(profile)}
      beta
    />
  );
}

function ThermalComfortTooltip() {
  return (
    <ThermalComfortTooltipContent>
      <ThermalComfortTooltipIntro>{t('numeroIndicaIndiceConfortoTermico')}</ThermalComfortTooltipIntro>
      <ThermalComfortTooltipSectionTitle>{t('tooltipConfortoComoInterpretar')}</ThermalComfortTooltipSectionTitle>
      <ThermalComfortTooltipList>
        <ThermalComfortTooltipListItem>
          <strong>{t('tooltipConfortoRuimLabel')}</strong>
          {' '}
          {t('tooltipConfortoRuimDesc')}
        </ThermalComfortTooltipListItem>
        <ThermalComfortTooltipListItem>
          <strong>{t('tooltipConfortoRegularLabel')}</strong>
          {' '}
          {t('tooltipConfortoRegularDesc')}
        </ThermalComfortTooltipListItem>
        <ThermalComfortTooltipListItem>
          <strong>{t('tooltipConfortoBomLabel')}</strong>
          {' '}
          {t('tooltipConfortoBomDesc')}
        </ThermalComfortTooltipListItem>
      </ThermalComfortTooltipList>
      <ThermalComfortTooltipDivider />
      <ThermalComfortTooltipFooter>
        <strong>{t('tooltipConfortoFooterBold')}</strong>
        {t('tooltipConfortoFooterNormal')}
      </ThermalComfortTooltipFooter>
    </ThermalComfortTooltipContent>
  );
}
